import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, KpiCard, Spinner,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, BookOpen, ChevronRight, FileSignature, Plus, RefreshCw, Scale,
} from 'lucide-react';

/**
 * Specialized list view for JournalEntry. KPIs an accountant actually
 * checks: total vouchers · total debits · total credits · unbalanced
 * count (any draft where dr ≠ cr that needs fixing).
 */

interface JeRow extends Record<string, unknown> {
  id?: number | string;
  journalEntryId?: string;
  voucherNumber?: string;
  voucherDate?: string;
  voucherType?: string;
  narration?: string;
  totalDebit?: number;
  totalCredit?: number;
  status?: string;
}

export default function JournalEntryList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<JeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiClient.get('/accounting/JournalEntry');
      const d = r.data;
      let list: JeRow[] = [];
      if (Array.isArray(d)) list = d as JeRow[];
      else if (d && typeof d === 'object') {
        const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
        if (Array.isArray(obj.items)) list = obj.items as JeRow[];
        else if (Array.isArray(obj.data)) list = obj.data as JeRow[];
        else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
          list = (obj.data as { items: unknown[] }).items as JeRow[];
        }
      }
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const kpis = useMemo(() => {
    const total = rows.length;
    let totalDebit = 0;
    let totalCredit = 0;
    let unbalanced = 0;
    for (const r of rows) {
      const d = Number(r.totalDebit ?? 0);
      const c = Number(r.totalCredit ?? 0);
      totalDebit += d;
      totalCredit += c;
      if (Math.abs(d - c) >= 0.01) unbalanced++;
    }
    return { total, totalDebit, totalCredit, unbalanced };
  }, [rows]);

  const columns = useMemo<DataGridColumn<JeRow>[]>(
    () => [
      { accessor: 'voucherNumber', title: 'Voucher #', sortable: true, filterable: true,
        render: (r) => (
          <Link
            to={`/panel/accounting/journal/${encodeURIComponent(String(r.journalEntryId ?? r.id ?? ''))}`}
            className="font-medium text-primary hover:underline"
          >
            {String(r.voucherNumber ?? r.id ?? '—')}
          </Link>
        )
      },
      { accessor: 'voucherDate', title: 'Date', sortable: true,
        render: (r) => fmtDate(r.voucherDate) },
      { accessor: 'voucherType', title: 'Type', sortable: true, filterable: true,
        render: (r) => <TypePill v={String(r.voucherType ?? 'Journal')} /> },
      { accessor: 'narration', title: 'Narration',
        render: (r) => (
          <span className="line-clamp-1 max-w-[360px] text-content-secondary">
            {String(r.narration ?? '—')}
          </span>
        )
      },
      { accessor: 'totalDebit', title: 'Debit', sortable: true,
        render: (r) => `₹ ${inr(Number(r.totalDebit ?? 0))}` },
      { accessor: 'totalCredit', title: 'Credit', sortable: true,
        render: (r) => `₹ ${inr(Number(r.totalCredit ?? 0))}` },
      { accessor: 'status', title: 'Status', sortable: true, filterable: true,
        render: (r) => <StatusPill v={String(r.status ?? '')} /> },
      { accessor: '__actions' as never, title: '', width: 50,
        render: (r) => (
          <Link
            to={`/panel/accounting/journal/${encodeURIComponent(String(r.journalEntryId ?? r.id ?? ''))}`}
            className="text-content-muted hover:text-primary"
          >
            <ChevronRight size={16} />
          </Link>
        ),
      },
    ],
    [],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Journal Vouchers</h1>
          <p className="text-xs text-content-muted">
            Double-entry book · live data from /api/accounting/JournalEntry
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/accounting/journal/new')}>
          <Plus className="h-3.5 w-3.5" /> New voucher
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Vouchers"
          value={kpis.total.toLocaleString()}
          subtitle="All statuses"
          icon={<BookOpen className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Total debit"
          value={`₹ ${inr(kpis.totalDebit)}`}
          subtitle="Sum of all entries"
          icon={<FileSignature className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Total credit"
          value={`₹ ${inr(kpis.totalCredit)}`}
          subtitle={`${
            Math.abs(kpis.totalDebit - kpis.totalCredit) < 0.01
              ? '✓ Books balance'
              : `Δ ₹ ${inr(Math.abs(kpis.totalDebit - kpis.totalCredit))}`
          }`}
          icon={<Scale className={`h-5 w-5 ${
            Math.abs(kpis.totalDebit - kpis.totalCredit) < 0.01 ? 'text-success' : 'text-warning'
          }`} />}
        />
        <KpiCard
          title="Unbalanced drafts"
          value={kpis.unbalanced.toLocaleString()}
          subtitle={kpis.unbalanced ? 'Need attention' : 'All clear'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.unbalanced ? 'text-danger' : 'text-success'}`} />}
        />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading journal entries…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No journal vouchers yet. Click <em>New voucher</em> to record your first one.
          </div>
        </Card>
      ) : (
        <DataGrid<JeRow>
          data={rows}
          columns={columns}
          rowId={(rows[0]?.journalEntryId != null ? 'journalEntryId' : 'id') as keyof JeRow & string}
        />
      )}
    </div>
  );
}

function TypePill({ v }: { v: string }) {
  const cls = 'border-info/40 bg-info/10 text-info';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] capitalize ${cls}`}>
      {v}
    </span>
  );
}

function StatusPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s === 'posted') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'draft') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'cancelled' || s === 'reversed') cls = 'border-danger/40 bg-danger/10 text-danger';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
